// KDU (Kaydedilmemiş değişiklik uyarısı) — kayıt defteri. Plan: Dokumanlar/Arayuz/KAYDEDILMEMIS-DEGISIKLIK-PLANI.md §4.1
//
// Her form bileşeni (mixins/kirliForm.js) created'da kendini buraya yazar, beforeUnmount'ta siler.
// Tek router guard'ı (router.js), başlık rozeti (SayfaBasligiAlani / SistemLayout) ve beforeunload
// dinleyicisi yalnız bu defteri okur — bileşen içi beforeRouteLeave yok (sekme çocukları, Dialog
// formları route bileşeni değil, orada beforeRouteLeave çalışmaz).
import { reactive, markRaw, watch } from "vue";

// uid (this.$.uid) → { kirliMi: () => boolean, muaf: (to) => boolean, etiket }
// Map reactive: kayıt ekleme/silme ve her kaydın okuduğu bileşen computed'ı (kirliMi) izlenir →
// kirliVarMi() bir computed/watch içinde çağrılınca rozet ve beforeunload kendiliğinden güncellenir.
const defter = reactive({ kayitlar: new Map() });
let onaySorucu = null; // layout mounted'ta verir: (baglam) => Promise<boolean>
let sonOnay = null; // { from, t } — onay sonrası guard yönlendirme zincirinde ikinci soru olmasın
let bekleyenBitir = null; // açık onay penceresinin çözücüsü (yeni soru gelirse eskisi "kal" sayılır)
let bekleyenConfirm = null; // o pencereyi açan $confirm (afterEach'te kapatmak için)

// Onaydan sonra mevcut beforeEach'in kendi yönlendirmesi (ör. saglayiciPanelHedefi) yeni bir gezinme
// başlatır; aynı "from" için bu süre içinde tekrar sorulmaz. afterEach/onError zaten sıfırlıyor —
// süre yalnız bir emniyet sınırı (yetki/refresh isteği yavaş ağda birkaç saniye sürebilir).
const ONAY_ZINCIR_MS = 10000;

// kancalar: { muaf: (to) => boolean, ayniYol: (to, from) => boolean, hemen: () => boolean }
// kirliMi reaktif (rozet, beforeunload dinleyicisini takma); hemen = beklemeden güncel durum
// (guard, beforeunload anı, Çıkış) — gecikmeli imzalı büyük formlarda ikisi ayrışır.
export function kaydet(uid, kirliMi, etiket, kancalar = {}) {
  defter.kayitlar.set(
    uid,
    markRaw({
      kirliMi,
      etiket: etiket || "",
      muaf: kancalar.muaf || null,
      ayniYol: kancalar.ayniYol || null,
      hemen: kancalar.hemen || null,
    })
  );
}

export function sil(uid) {
  defter.kayitlar.delete(uid);
}

// Bir formun kirliAlanlar()/kirliMi hesabı hata fırlatırsa gezinme, rozet ve beforeunload
// kilitlenmesin: hata = kirli değil (sessiz yutulmaz, konsola yazılır).
function guvenliKirli(k, hemen = false) {
  try {
    return !!(hemen && k.hemen ? k.hemen() : k.kirliMi());
  } catch (e) {
    console.error("KDU", k.etiket, e);
    return false;
  }
}

function guvenliKanca(k, ad, ...args) {
  const fn = k[ad];
  if (!fn) return false;
  try {
    return !!fn(...args);
  } catch (e) {
    console.error("KDU", k.etiket, ad, e);
    return false;
  }
}

// Reaktif (computed/watch içinde): rozet, beforeunload dinleyicisi
export function kirliVarMi() {
  for (const k of defter.kayitlar.values()) if (guvenliKirli(k)) return true;
  return false;
}

// Anlık (karar anı): router guard, beforeunload olayı, Çıkış düğmesi
export function kirliVarMiHemen() {
  for (const k of defter.kayitlar.values()) if (guvenliKirli(k, true)) return true;
  return false;
}

// V10: kirli formların hepsi bu hedefi serbest bırakıyorsa sorulmaz (TeklifOlustur önizleme).
export function aktifSayfaMuafMi(to) {
  let kirliSayisi = 0;
  for (const k of defter.kayitlar.values()) {
    if (!guvenliKirli(k, true)) continue;
    kirliSayisi++;
    if (!guvenliKanca(k, "muaf", to)) return false;
  }
  return kirliSayisi > 0;
}

// V6 istisnası: aynı path'te (yalnız query/hash değişimi) normalde sorulmaz; kayıt kimliği
// query'de taşınan sayfa (ör. /musteri/duzenlemusteri?MusteriNo=) kirliAyniYolSorar ile
// "başka kayda geçiş" olduğunu söylerse sorulur.
export function ayniYolSorulurMu(to, from) {
  for (const k of defter.kayitlar.values()) {
    if (!guvenliKirli(k, true)) continue;
    if (guvenliKanca(k, "ayniYol", to, from)) return true;
  }
  return false;
}

// Gönüllü çıkış (Logout) onaylandıktan sonra: sonraki push('/auth/login') + reload() tekrar sormasın.
export function defteriBosalt() {
  defter.kayitlar.clear();
}

export function onaySorucuAyarla(fn) {
  onaySorucu = fn;
}

// Layout unmount: yalnız kendi verdiği sorucuyu kaldırır (Admin ↔ Sistem layout geçişinde yeni
// layout'un sorucusunu silmesin).
export function onaySorucuKaldir(fn) {
  if (onaySorucu === fn) onaySorucu = null;
}

// Promise<boolean>: true = "Kaydetmeden çık", false = "Sayfada kal". Sorucu yoksa (public
// sayfalar, KDU-7'ye kadar) tarayıcının window.confirm'ü.
export function cikisOnayiAl(baglam = "sayfa") {
  if (onaySorucu) return onaySorucu(baglam);
  return Promise.resolve(window.confirm("Kaydedilmemiş değişiklikler var. Kaydetmeden çıkılsın mı?"));
}

export function onayVerildi(from) {
  sonOnay = { from, t: Date.now() };
}

export function onayGecerliMi(from) {
  return !!sonOnay && sonOnay.from === from && Date.now() - sonOnay.t < ONAY_ZINCIR_MS;
}

export function onaySifirla() {
  sonOnay = null;
}

// Başka bir gezinme (sormadan) tamamlandıysa ekranda kalan cevapsız onay penceresi kapatılır,
// bekleyen guard "Sayfada kal" (false) ile çözülür. Yalnız bekleyen soru varken çalışır —
// $confirm.close() tüm ConfirmDialog'ları kapattığı için boşuna çağrılmaz.
export function bekleyenOnayiKapat() {
  if (!bekleyenBitir) return;
  const c = bekleyenConfirm;
  bekleyenBitir(false);
  try {
    if (c) c.close();
  } catch (e) {
    /* pencere zaten kapalı */
  }
}

// Layout'ların ortak sorucusu (§4.4, K4 iki düğme): PrimeVue $confirm, group="kirli" —
// sayfa silme onaylarının grupsuz ConfirmDialog'uyla çakışmaz.
export function confirmSorucusu(confirm) {
  return (baglam) =>
    new Promise((coz) => {
      if (bekleyenBitir) bekleyenBitir(false); // önceki cevapsız soru "Sayfada kal" sayılır
      let bitti = false;
      const bitir = (v) => {
        if (bitti) return;
        bitti = true;
        if (bekleyenBitir === bitir) {
          bekleyenBitir = null;
          bekleyenConfirm = null;
        }
        coz(v);
      };
      bekleyenBitir = bitir;
      bekleyenConfirm = confirm;
      confirm.require({
        group: "kirli",
        header: "Kaydedilmemiş değişiklikler var",
        message:
          baglam === "dialog"
            ? "Bu pencereyi kapatırsanız yaptığınız değişiklikler kaybolacak."
            : "Bu sayfadan ayrılırsanız yaptığınız değişiklikler kaybolacak.",
        icon: "pi pi-exclamation-triangle",
        rejectLabel: "Sayfada kal",
        rejectProps: { label: "Sayfada kal", severity: "secondary", outlined: true },
        acceptLabel: "Kaydetmeden çık",
        acceptProps: { label: "Kaydetmeden çık", severity: "danger" },
        defaultFocus: "reject",
        accept: () => bitir(true),
        reject: () => bitir(false),
        onHide: () => bitir(false),
      });
    });
}

// MDN: beforeunload dinleyicisi yalnız kaydedilmemiş değişiklik varken takılı (hep takılıysa
// bfcache kapanır). Tarayıcı kendi metnini gösterir; iOS Safari desteklemez (kabul, plan §8).
function sayfaKapaniyor(e) {
  if (!kirliVarMiHemen()) return undefined; // kirliVarMi her kaydı try/catch ile okur
  e.preventDefault();
  e.returnValue = "";
  return "";
}

let dinleyiciTakili = false;
watch(kirliVarMi, (kirli) => {
  if (kirli && !dinleyiciTakili) {
    window.addEventListener("beforeunload", sayfaKapaniyor);
    dinleyiciTakili = true;
  } else if (!kirli && dinleyiciTakili) {
    window.removeEventListener("beforeunload", sayfaKapaniyor);
    dinleyiciTakili = false;
  }
});
